/**
 * A transient fault handling module
 *
 * Inspired by the Retry policy family portion of the Polly .NET library
 * (https://github.com/App-vNext/Polly)
 */
var SCM = SCM || {};
SCM.Retry = (function () {
    var moduleDefinition = {
        policy: function () { return new RetryPolicy(); },
        wait: _buildWaitPolicy
    };

    function _buildWaitPolicy(timeInMs) {
        return new RetryPolicy()
            .handleResult(function() { return true; })
            .waitAndRetry(1, timeInMs)
            .execute(function () { return; })
            .catch(function() { return; });
    }

    var DefaultWaitTimeCalculator = function (retryNumber) { return 50; };

    function RetryPolicy() {
        this._exceptionPredicates = [];
        this._resultPredicates = [];
        this._retryCount = 0;
        this._onRetry = null;
        this._waitTimeCalculator = DefaultWaitTimeCalculator;
    }
    /**
     * 
     * @param {(exception: Error) => boolean} [exceptionPredicate] a function that takes the generated
     *  exception as its single parameter, and returns true if a retry should occur.
     *  If no predicate is provided, all Exceptions will result in a retry.
     * @returns {RetryPolicy}
     */
    RetryPolicy.prototype.handleException = function (exceptionPredicate) {
        if (typeof exceptionPredicate === 'undefined') {
            this._exceptionPredicates.push(function () { return true; });
        } else if (typeof exceptionPredicate !== 'function') {
            throw new RetryError('exceptionPredicate must be a function');
        } else {
            this._exceptionPredicates.push(exceptionPredicate);
        }
        return this;
    };
    /**
     *
     * @param {(result: any) => boolean} resultPredicate a function that takes the generated
     *  response as its single parameter, and returns true if a retry should occur.
     * @returns {RetryPolicy}
     */
    RetryPolicy.prototype.handleResult = function (resultPredicate) {
        if (typeof resultPredicate !== 'function') {
            throw new RetryError('resultPredicate must be a function');
        }
        this._resultPredicates.push(resultPredicate);
        return this;
    };
    /**
     *
     * @param {any} desiredResult
     * @returns {RetryPolicy}
     */
    RetryPolicy.prototype.untilResultIs = function (desiredResult) {
        this._resultPredicates.push(function (result) { return result != desiredResult; });
        return this;
    };
    /**
     * Builds a {RetryPolicy} that will retry {retryCount} times and call {onRetry} on each retry.
     * @param {number} [retryCount] the number of times to retry.  Default is 1.
     * @param {(exceptionOrResult: Error|any, retryNumber: number) => void} [onRetry] A function that is called on each retry.
     * It takes the Error or result and retry number as parameters
     * @returns {RetryPolicy}
     */
    RetryPolicy.prototype.retry = function (retryCount, onRetry) {
        if (typeof retryCount === 'undefined') {
            retryCount = 1;
        }
        if (typeof retryCount === 'function') {
            onRetry = retryCount;
            retryCount = 1;
        }

        this._retryCount = retryCount;
        if (typeof onRetry === 'function') {
            this._onRetry = onRetry;
        }
        return this;
    };
    /**
     * 
     * @param {number} retryCount
     * @param {number|number[]|Function} waitTimeConfiguration
     * @param {(exceptionOrResult: Error|any, retryNumber: number) => void} [onRetry]
     * @return {RetryPolicy}
     */
    RetryPolicy.prototype.waitAndRetry = function (retryCount, waitTimeConfiguration, onRetry) {
        if (typeof retryCount !== 'number') {
            throw new RetryError('retryCount must be specified as an integer');
        }
        this._retryCount = retryCount;
        if (typeof waitTimeConfiguration !== 'undefined') {
            this._waitTimeCalculator = _determineWaitTimeCalculator(waitTimeConfiguration, retryCount);
        }
        if (typeof onRetry === 'function') {
            this._onRetry = onRetry;
        }
        return this;
    };
    /**
     *
     * @param {() => any} action
     * @returns {Promise} A Promise that resolves to the return value of the {action} function parameter;
     *  or rejects with an Error
     */
    RetryPolicy.prototype.execute = function (action) {
        var resultPredicates = this._resultPredicates;
        var exceptionPredicates = this._exceptionPredicates;
        var onRetry = this._onRetry;
        var waitTimeCalculator = this._waitTimeCalculator;
        var retryCount = this._retryCount;

        return new Promise(function (resolve, reject) {
            var count = 0;

            function __internalExecute() {
                try {
                    var result = action();
                    if (_isPromiseLike(result)) {
                        result
                            .then(function (result) {
                                __internalCheckResult(result);
                            })
                            .catch(function (reason) {
                                __internalCheckException(reason);
                            });
                    } else {
                        __internalCheckResult(result);
                    }
                } catch (ex) {
                    __internalCheckException(ex);
                }
            }

            function __internalCheckResult(result) {
                if (!resultPredicates.some(function (p) { return p(result); })) {
                    resolve(result);
                } else {
                    if (count < retryCount) {
                        count++;
                        if (typeof onRetry === 'function') {
                            onRetry(result, count);
                        }
                        setTimeout(__internalExecute, waitTimeCalculator(count));
                    } else {
                        reject(new RetryError('retryCount exceeded'));
                    }
                }
            }

            function __internalCheckException(ex) {
                if (count < retryCount && exceptionPredicates.some(function (p) { return p(ex); })) {
                    count++;
                    if (typeof onRetry === 'function') {
                        onRetry(ex, count);
                    }
                    setTimeout(__internalExecute, waitTimeCalculator(count));
                } else {
                    reject(ex);
                }
            }

            __internalExecute();
        });
    };

    function RetryError(message) {
        var instance = new Error(message);
        instance.name = 'RetryError';
        Object.setPrototypeOf(instance, Object.getPrototypeOf(this));
        if (Error.captureStackTrace) {
            Error.captureStackTrace(instance, RetryError);
        }
        return instance;
    }
    RetryError.prototype = Object.create(Error.prototype, {
        constructor: {
            value: Error,
            enumerable: false,
            writable: true,
            configurable: true
        }
    });
    if (Object.setPrototypeOf) {
        Object.setPrototypeOf(RetryError, Error);
    } else {
        RetryError.__proto__ = Error;
    }

    /**
     * 
     * @param {number|number[]|Function} waitTimeConfiguration
     * @param {number} retryCount
     * @return {(retryNumber: number) => number}
     */
    function _determineWaitTimeCalculator(waitTimeConfiguration, retryCount) {
        if (typeof waitTimeConfiguration === 'number') {
            return function (retryNumber) { return waitTimeConfiguration; };
        }
        if (Array.isArray(waitTimeConfiguration) && waitTimeConfiguration.length === retryCount) {
            if (waitTimeConfiguration.length !== retryCount) {
                throw new RetryError('Wait time array length must match retryCount');
            }
            return function (retryNumber) { return waitTimeConfiguration[retryNumber - 1]; };
        }
        if (typeof waitTimeConfiguration === 'function') {
            return waitTimeConfiguration;
        }
        return DefaultWaitTimeCalculator;
    }

    function _isPromiseLike(obj) {
        return obj &&
            typeof obj.then === 'function' &&
            typeof obj.catch === 'function';
    }

    return moduleDefinition;
})();

// make the wait function more globally available
SCM.wait = SCM.wait || SCM.Retry.wait;

// If we are in a module context, export this module (required for unit test)
'object' === typeof module && module.exports && (module.exports = SCM.Retry);
